import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  UsageAggregationFilter,
  UsageAggregationFrequency,
  UsageAggregationService,
} from '@markant-platform/usage/data-access';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MarkantElementsModule, MarkantSelectOption } from '@markant/elements-angular';
import { UiCardComponent } from '@markant-platform/shared/ui-card';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CartridgeServicePackage } from '@markant-platform/cartridge/model';
import { BillingPeriod } from '@markant-platform/shared/backoffice/model';
import { endOfDay, formatted, isDate, isSameDate, parse, startOfDay } from '@markant-platform/shared/util-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';

@Component({
  selector: 'markant-platform-usage-report-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MarkantElementsModule,
    ReactiveFormsModule,
    UiCardComponent,
    TranslocoModule,
    FormControlKeyErrorPipe,
  ],
  templateUrl: './usage-report-filter.component.html',
  styleUrls: ['./usage-report-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageReportFilterComponent implements OnInit {
  @Input({ required: true }) servicePackages!: CartridgeServicePackage[];
  @Input() usageStartedAt?: string;
  @Input() billingPeriod?: BillingPeriod;
  @Input() servicePackage?: CartridgeServicePackage;

  @Output() filterChanged: EventEmitter<UsageAggregationFilter> = new EventEmitter<UsageAggregationFilter>();

  isCurrentDeliveryPeriod = true;
  dateFormat!: string;

  protected readonly formGroup: FormGroup = new FormGroup({
    servicePackageId: new FormControl('', { nonNullable: true, validators: Validators.required }),
    frequency: new FormControl('', { nonNullable: true, validators: Validators.required }),
    timePeriod: new FormControl('', { nonNullable: true, validators: Validators.required }),
  });

  protected servicePackageOptions: MarkantSelectOption[] = [];
  protected frequencyOptions = this.getFrequencyOptions();

  private currentDeliveryPeriod: Date[] = [];

  constructor(
    private readonly usageAggregationService: UsageAggregationService,
    private readonly translocoService: TranslocoService,
    private readonly destroyRef: DestroyRef
  ) {
    this.translocoService.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.servicePackageOptions = this.getServicePackageOptions();
      this.frequencyOptions = this.getFrequencyOptions();
    });
  }

  ngOnInit(): void {
    if (this.usageStartedAt && this.billingPeriod) {
      this.currentDeliveryPeriod = this.usageAggregationService.getCurrentDeliveryPeriod(
        new Date(this.usageStartedAt),
        this.billingPeriod
      );
    }

    const [servicePackage] = this.servicePackages;
    this.servicePackageOptions = this.getServicePackageOptions();

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onFilterChanged();
    });

    this.formGroup.patchValue({
      servicePackageId: servicePackage.id,
      frequency: this.getDefaultFrequencyForBillingPeriod(this.billingPeriod),
      timePeriod: this.getTimePeriodValue(),
    });
  }

  private getServicePackageOptions(): MarkantSelectOption[] {
    return this.servicePackages.map(servicePackage => ({
      value: servicePackage.id,
      representation: servicePackage.name,
    })) as MarkantSelectOption[];
  }

  private getFrequencyOptions(): MarkantSelectOption[] {
    return Object.values(UsageAggregationFrequency).map(frequency => ({
      value: frequency,
      representation: this.translocoService.translate('usage.report.filter.form.interval.option.' + frequency),
    })) as MarkantSelectOption[];
  }

  onFilterChanged(): void {
    if (this.formGroup.invalid) {
      return;
    }

    const { servicePackageId, frequency, timePeriod } = this.formGroup.getRawValue();

    let [periodStart, periodEnd]: Date[] = this.resolveDateRange(timePeriod);

    const servicePackage: CartridgeServicePackage | undefined = this.servicePackages.find(
      servicePackage => servicePackage.id === servicePackageId
    );
    const serviceIds: string[] = servicePackage?.cartridgeServices.map(cs => cs.id) ?? [];

    if (servicePackage && isDate(periodStart) && isDate(periodEnd) && periodStart <= periodEnd) {
      periodStart = startOfDay(periodStart);
      periodEnd = endOfDay(periodEnd);

      this.filterChanged.emit({
        servicePackageId,
        serviceIds,
        frequency,
        periodStart,
        periodEnd,
      });

      this.isCurrentDeliveryPeriod = this.checkDateRanges(periodStart, periodEnd);
    }
  }

  showCurrentTimePeriod(checked: boolean): void {
    if (checked) {
      const { timePeriod } = this.formGroup.controls;

      timePeriod.setValue(this.getTimePeriodValue());

      this.isCurrentDeliveryPeriod = true;
    }
  }

  protected trackBy(index: number): number {
    return index;
  }

  private getDefaultFrequencyForBillingPeriod(billingPeriod?: BillingPeriod): UsageAggregationFrequency {
    if (billingPeriod === 'MONTHLY') {
      return UsageAggregationFrequency.DAILY;
    }

    return UsageAggregationFrequency.MONTHLY;
  }

  private checkDateRanges(periodStart: Date, periodEnd: Date): boolean {
    let deliveryStartDate: Date | undefined = undefined;
    let deliveryEndDate: Date | undefined = undefined;

    if (this.currentDeliveryPeriod.length) {
      deliveryStartDate = startOfDay(this.currentDeliveryPeriod[0]);
      deliveryEndDate = endOfDay(this.currentDeliveryPeriod[1]);
    }

    if (!!deliveryStartDate && !!deliveryEndDate) {
      return isSameDate(deliveryStartDate, periodStart) && isSameDate(deliveryEndDate, periodEnd);
    } else {
      return false;
    }
  }

  private resolveDateRange(range: string): Date[] {
    const format = this.translocoService.translate('common.dateTime.format.date');
    const [periodStart, periodEnd]: Date[] = range.split(' - ').map((date: string) => parse(date, format));

    return [periodStart, periodEnd || new Date(periodStart)];
  }

  private getTimePeriodValue(): string {
    const lang = this.translocoService.getActiveLang();
    return this.currentDeliveryPeriod.map(date => formatted(date, lang)).join(' - ');
  }
}
